import { env } from "@shared/config";
import { buildPath } from "./build-path";

/**
 * The endpoint map.
 *
 * Unlike the old monorepo's `API_ROUTES`, this is **hand-maintained** — there
 * is no TypeScript backend to generate it from. When a route changes, verify
 * against `php artisan route:list --path=api --except-vendor` and update here.
 *
 * Paths are absolute and version-prefixed; the `/api` global prefix lives in
 * `env.api.url`. The actor segment is parameterized because cubeta-starter
 * generates one route file per actor (`routes/v1/api/admin.php` today,
 * `protected.php` is an empty stub waiting for a second one) — so adding a
 * `user` actor later is a config change, not a rewrite.
 */
export const createApiRoutes = (actor: string) => {
  const root = `/v1/${actor}`;
  const games = `${root}/games`;

  return {
    AUTH: {
      base: root,
      login: () => `${root}/login`,
      register: () => `${root}/register`,
      /** **GET**, not POST — see `routes/v1/api/admin.php`. */
      logout: () => `${root}/logout`,
      /**
       * Takes no body grant: it authenticates off the `Authorization` header,
       * so send the stored `refresh_token` as the Bearer credential.
       */
      refresh: () => `${root}/refresh`,
      /**
       * `GET` returns the user **flat** in `data`; `PUT` updates it and
       * re-issues the whole token pair.
       */
      me: () => `${root}/me`,
      requestResetPassword: () => `${root}/request-reset-password`,
      validateResetPasswordCode: () => `${root}/validate-reset-password-code`,
      resetPassword: () => `${root}/reset-password`,
    },
    GAME: {
      base: games,
      findOne: (id: string | number) => buildPath(games, id),
      /** Returns a binary XLSX, not the envelope — not wired to the client. */
      export: () => `${games}/export`,
      import: () => `${games}/import`,
      importExample: () => `${games}/get-import-example`,
    },
  } as const;
};

export type ApiRoutes = ReturnType<typeof createApiRoutes>;

export const API_ROUTES = createApiRoutes(env.api.actor);
